import React, { useState, useEffect, useRef } from 'react';
import { Gallery, DisplaySettings } from '../../types/Gallery';
import { loadLiveCategories } from '../../utils/CategoryAPI';
import ImageCard from './ImageCard';
import GalleryModal from './GalleryModal';
import './CarouselGallery.css';

interface CarouselGalleryProps {
  galleries: Gallery[];
  settings: DisplaySettings;
}

const CarouselGallery: React.FC<CarouselGalleryProps> = ({ galleries, settings }) => {
  // Ensure galleryCount doesn't exceed actual available galleries
  const maxGalleries = settings.galleryCount || galleries.length;
  const actualGalleryCount = Math.min(maxGalleries, galleries.length);
  const displayGalleries = galleries.slice(0, actualGalleryCount);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedGallery, setSelectedGallery] = useState<Gallery | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoPlayIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleGalleryClick = (gallery: Gallery) => {
    setSelectedGallery(gallery);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedGallery(null);
  };

  // Load categories
  useEffect(() => {
    const loadCategoriesData = async () => {
      try {
        const categoriesData = await loadLiveCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategoriesData();

    const handleCategoryUpdate = () => {
      setTimeout(() => {
        loadCategoriesData();
      }, 100);
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'admin_categories') {
        setTimeout(() => {
          loadCategoriesData();
        }, 100);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('category_updated', handleCategoryUpdate);
    window.addEventListener('categories_updated', handleCategoryUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('category_updated', handleCategoryUpdate);
      window.removeEventListener('categories_updated', handleCategoryUpdate);
    };
  }, []);

  // Get category color
  const getCategoryColor = (categoryName: string): string => {
    const normalizedInput = (categoryName || '').toLowerCase().trim();
    const category = categories.find(cat =>
      (cat.name || '').toLowerCase().trim() === normalizedInput ||
      (cat.slug || '').toLowerCase().trim() === normalizedInput
    );
    return category?.color || '#8B4513';
  };

  // Apply theme settings
  useEffect(() => {
    const loadThemeAndApply = () => {
      const currentTheme = localStorage.getItem('admin-theme');
      if (currentTheme) {
        try {
          const themeData = JSON.parse(currentTheme);
          const root = document.documentElement;

          if (themeData.palette) {
            root.style.setProperty('--color-primary', themeData.palette.primary || '#8B4513');
            root.style.setProperty('--color-secondary', themeData.palette.secondary || '#A0522D');
            root.style.setProperty('--color-accent', themeData.palette.accent || '#D4AF37');
            root.style.setProperty('--color-background', themeData.palette.background || '#FFFFFF');
            root.style.setProperty('--color-text', themeData.palette.text || '#8B4513');
            root.style.setProperty('--color-surface', themeData.palette.surface || '#F8F9FA');
            root.style.setProperty('--color-border', themeData.palette.border || '#E1E1E1');
          }
        } catch (error) {
          console.error('Error loading theme settings:', error);
        }
      }
    };

    loadThemeAndApply();

    const handleThemeChange = () => {
      loadThemeAndApply();
    };

    window.addEventListener('storage', handleThemeChange);
    window.addEventListener('theme_updated', handleThemeChange);
    
    return () => {
      window.removeEventListener('storage', handleThemeChange);
      window.removeEventListener('theme_updated', handleThemeChange);
    };
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (displayGalleries.length <= 1) return;

    autoPlayIntervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % displayGalleries.length);
    }, 5000);

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
    };
  }, [displayGalleries.length]);

  // Update carousel position
  useEffect(() => {
    if (carouselRef.current) {
      const cardWidth = carouselRef.current.offsetWidth / Math.min(3, displayGalleries.length);
      const translateX = -currentIndex * cardWidth;
      carouselRef.current.style.transform = `translateX(${translateX}px)`;
    }
  }, [currentIndex, displayGalleries.length]);

  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % displayGalleries.length);
  };

  const prevSlide = () => {
    setCurrentIndex(prev => prev === 0 ? displayGalleries.length - 1 : prev - 1);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  if (displayGalleries.length === 0) {
    return (
      <div className="carousel-gallery-container">
        <div className="no-galleries">
          <h2>Gallery Coming Soon</h2>
          <p>My curated photography collection will be available shortly...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="carousel-gallery-container" data-count={displayGalleries.length}>
        <div className="carousel-viewport">
          <div 
            className="carousel-track"
            ref={carouselRef}
          >
            {displayGalleries.map((gallery) => (
              <div key={gallery.id} className="carousel-slide">
                <ImageCard 
                  gallery={gallery}
                  showcase={true}
                  className="carousel-card"
                  onGalleryClick={handleGalleryClick}
                  categoryColor={getCategoryColor(gallery.category)}
                />
              </div>
            ))}
          </div>

          {/* Navigation Controls */}
          {displayGalleries.length > 1 && (
            <>
              <button 
                className="carousel-nav carousel-prev"
                onClick={prevSlide}
                aria-label="Previous gallery"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button 
                className="carousel-nav carousel-next"
                onClick={nextSlide}
                aria-label="Next gallery"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </>
          )}
        </div>

        {/* Indicators */}
        {displayGalleries.length > 1 && (
          <div className="carousel-indicators">
            {displayGalleries.map((_, index) => (
              <button
                key={index}
                className={`carousel-indicator ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      <GalleryModal 
        gallery={selectedGallery}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </>
  );
};

export default React.memo(CarouselGallery);
